import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Button, Chip, CircularProgress, Alert, Card, CardContent,
    Grid, Avatar, useTheme, useMediaQuery, TablePagination, FormControl,
    InputLabel, Select, MenuItem, LinearProgress, Divider
} from '@mui/material';
import {
    TrendingUp, Store, AttachMoney, ShoppingCart, Inventory, Star,
    Timeline, BarChart, PieChart, Speed, LocalShipping, CheckCircle,
    Pending, Warning, ArrowUpward, ArrowDownward, Remove
} from '@mui/icons-material';
import adminService from '../services/adminService';

// Base URL for serving uploaded static files
const ASSET_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://xaintro-backend.onrender.com';

const SellerPerformance = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [loading, setLoading] = useState(true);
    const [sellers, setSellers] = useState([]);
    const [error, setError] = useState(null);
    const [sellerOrdersData, setSellerOrdersData] = useState({});
    const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'quarter', 'year'
    const [metric, setMetric] = useState('revenue'); // 'revenue', 'orders', 'products', 'rating'

    // Pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const loadSellerPerformance = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await adminService.getSellers();
            // Only include verified sellers for performance analytics
            const verifiedSellers = response.sellers.filter(seller => seller.sellerStatus === 'verified');
            setSellers(verifiedSellers);

            // Fetch completed orders for each seller
            const ordersPromises = verifiedSellers.map(async (seller) => {
                try {
                    const ordersResponse = await adminService.getSellerOrders(seller._id);
                    return {
                        sellerId: seller._id,
                        ordersData: ordersResponse.data
                    };
                } catch (err) {
                    console.error(`Failed to load orders for seller ${seller._id}:`, err);
                    return {
                        sellerId: seller._id,
                        ordersData: null
                    };
                }
            });

            const ordersResults = await Promise.all(ordersPromises);
            
            // Build orders data object
            const ordersDataMap = {};
            ordersResults.forEach(result => {
                if (result.ordersData) {
                    ordersDataMap[result.sellerId] = result.ordersData;
                }
            });
            
            setSellerOrdersData(ordersDataMap);
        } catch (err) {
            console.error('Failed to load seller performance:', err);
            setError('Failed to load seller performance data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSellerPerformance();
    }, [loadSellerPerformance]);

    const getPerformanceTrend = (current, previous) => {
        if (!previous || previous === 0) return { trend: 'stable', value: 0 };
        const change = ((current - previous) / previous) * 100;
        if (change > 5) return { trend: 'up', value: Math.abs(change) };
        if (change < -5) return { trend: 'down', value: Math.abs(change) };
        return { trend: 'stable', value: Math.abs(change) };
    };

    const getPerformanceLevel = (seller) => {
        const ordersData = sellerOrdersData[seller._id];
        if (!ordersData) return 'needs_improvement';
        
        const revenue = ordersData.totalRevenue || 0;
        const orders = ordersData.totalOrders || 0;
        const products = seller.stats?.totalProducts || 0;
        
        if (revenue >= 100000 && orders >= 100 && products >= 50) return 'excellent';
        if (revenue >= 50000 && orders >= 50 && products >= 20) return 'good';
        if (revenue >= 10000 && orders >= 10 && products >= 5) return 'average';
        return 'needs_improvement';
    };

    const getPerformanceColor = (level) => {
        switch (level) {
            case 'excellent': return 'success';
            case 'good': return 'success';
            case 'average': return 'warning';
            case 'needs_improvement': return 'error';
            default: return 'default';
        }
    };

    const getPerformanceIcon = (trend) => {
        switch (trend) {
            case 'up': return <ArrowUpward sx={{ fontSize: 16, color: '#4CAF50' }} />;
            case 'down': return <ArrowDownward sx={{ fontSize: 16, color: '#F44336' }} />;
            default: return <Remove sx={{ fontSize: 16, color: '#FF9800' }} />;
        }
    };

    const getPerformanceStats = () => {
        const levels = sellers.map(seller => getPerformanceLevel(seller));
        const stats = {
            excellent: levels.filter(l => l === 'excellent').length,
            good: levels.filter(l => l === 'good').length,
            average: levels.filter(l => l === 'average').length,
            needs_improvement: levels.filter(l => l === 'needs_improvement').length,
        };
        return stats;
    };

    const getTopPerformers = () => {
        return sellers
            .sort((a, b) => {
                const aOrdersData = sellerOrdersData[a._id];
                const bOrdersData = sellerOrdersData[b._id];
                const aRevenue = aOrdersData?.totalRevenue || 0;
                const bRevenue = bOrdersData?.totalRevenue || 0;
                return bRevenue - aRevenue;
            })
            .slice(0, 10);
    };

    const performanceStats = getPerformanceStats();
    const topPerformers = getTopPerformers();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUp sx={{ fontSize: 32, color: '#3b82f6' }} />
                    <Box>
                        <Typography variant="h5" fontWeight={700}>
                            Seller Performance Analytics
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Comprehensive performance metrics and analytics for verified sellers
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip label={`${sellers.length} Sellers`} color="primary" sx={{ fontWeight: 600 }} />
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Time Range and Metric Filters */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Time Range</InputLabel>
                            <Select
                                value={timeRange}
                                label="Time Range"
                                onChange={(e) => setTimeRange(e.target.value)}
                            >
                                <MenuItem value="week">Last Week</MenuItem>
                                <MenuItem value="month">Last Month</MenuItem>
                                <MenuItem value="quarter">Last Quarter</MenuItem>
                                <MenuItem value="year">Last Year</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Primary Metric</InputLabel>
                            <Select
                                value={metric}
                                label="Primary Metric"
                                onChange={(e) => setMetric(e.target.value)}
                            >
                                <MenuItem value="revenue">Revenue</MenuItem>
                                <MenuItem value="orders">Orders</MenuItem>
                                <MenuItem value="products">Products</MenuItem>
                                <MenuItem value="rating">Customer Rating</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Performance Overview Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: '#e8f5e9' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Star sx={{ fontSize: 40, color: '#4CAF50' }} />
                            <Typography variant="h4" fontWeight={700}>{performanceStats.excellent}</Typography>
                            <Typography variant="body2" color="text.secondary">Excellent</Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={sellers.length > 0 ? (performanceStats.excellent / sellers.length) * 100 : 0} 
                                sx={{ mt: 1, bgcolor: '#c8e6c9' }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: '#f0f4c3' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <TrendingUp sx={{ fontSize: 40, color: '#8BC34A' }} />
                            <Typography variant="h4" fontWeight={700}>{performanceStats.good}</Typography>
                            <Typography variant="body2" color="text.secondary">Good</Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={sellers.length > 0 ? (performanceStats.good / sellers.length) * 100 : 0} 
                                sx={{ mt: 1, bgcolor: '#dce775' }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: '#fff3e0' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Speed sx={{ fontSize: 40, color: '#FF9800' }} />
                            <Typography variant="h4" fontWeight={700}>{performanceStats.average}</Typography>
                            <Typography variant="body2" color="text.secondary">Average</Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={sellers.length > 0 ? (performanceStats.average / sellers.length) * 100 : 0} 
                                sx={{ mt: 1, bgcolor: '#ffe0b2' }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ bgcolor: '#ffebee' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Warning sx={{ fontSize: 40, color: '#F44336' }} />
                            <Typography variant="h4" fontWeight={700}>{performanceStats.needs_improvement}</Typography>
                            <Typography variant="body2" color="text.secondary">Needs Improvement</Typography>
                            <LinearProgress 
                                variant="determinate" 
                                value={sellers.length > 0 ? (performanceStats.needs_improvement / sellers.length) * 100 : 0} 
                                sx={{ mt: 1, bgcolor: '#ffcdd2' }}
                            />
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Key Metrics Summary */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e3f2fd' }}>
                        <AttachMoney sx={{ fontSize: 32, color: '#2196F3' }} />
                        <Typography variant="h5" fontWeight={700} color="#2196F3">
                            PKR {Object.values(sellerOrdersData).reduce((sum, data) => sum + (data?.totalRevenue || 0), 0).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Revenue</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#f3e5f5' }}>
                        <ShoppingCart sx={{ fontSize: 32, color: '#9C27B0' }} />
                        <Typography variant="h5" fontWeight={700} color="#9C27B0">
                            {Object.values(sellerOrdersData).reduce((sum, data) => sum + (data?.totalOrders || 0), 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Orders</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#e8f5e9' }}>
                        <Inventory sx={{ fontSize: 32, color: '#4CAF50' }} />
                        <Typography variant="h5" fontWeight={700} color="#4CAF50">
                            {sellers.reduce((sum, s) => sum + (s.stats?.totalProducts || 0), 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Products</Typography>
                    </Paper>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2, textAlign: 'center', bgcolor: '#fff8e1' }}>
                        <BarChart sx={{ fontSize: 32, color: '#FFC107' }} />
                        <Typography variant="h5" fontWeight={700} color="#FFC107">
                            PKR {Object.values(sellerOrdersData).length > 0 ? Math.round(Object.values(sellerOrdersData).reduce((sum, data) => sum + (data?.totalRevenue || 0), 0) / Object.values(sellerOrdersData).length).toLocaleString() : 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Avg Revenue/Seller</Typography>
                    </Paper>
                </Grid>
            </Grid>

            {/* Top Performers */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Star sx={{ color: '#FFD700' }} />
                    Top 10 Performers
                </Typography>
                <Grid container spacing={2}>
                    {topPerformers.map((seller, index) => {
                        const level = getPerformanceLevel(seller);
                        const color = getPerformanceColor(level);
                        const borderHex = level === 'excellent' ? '#4CAF5020' : level === 'good' ? '#8BC34A20' : level === 'average' ? '#FF980020' : '#F4433620';
                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={seller._id}>
                                <Paper sx={{ p: 2, bgcolor: '#fafafa', border: `2px solid ${borderHex}` }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                        <Chip 
                                            label={`#${index + 1}`} 
                                            size="small" 
                                            sx={{ 
                                                bgcolor: index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : index === 2 ? '#CD7F32' : '#9E9E9E',
                                                color: 'white',
                                                fontWeight: 700
                                            }} 
                                        />
                                        <Avatar 
                                            src={seller.profileImage}
                                            sx={{ width: 32, height: 32, bgcolor: color }}
                                        >
                                            <Store sx={{ fontSize: 16 }} />
                                        </Avatar>
                                        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {seller.brandName || seller.name}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Revenue
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600} color={color}>
                                            PKR {(sellerOrdersData[seller._id]?.totalRevenue || 0).toLocaleString()}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Orders
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {sellerOrdersData[seller._id]?.totalOrders || 0}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={level.replace('_', ' ').toUpperCase()}
                                        size="small"
                                        sx={{ 
                                            mt: 1,
                                            bgcolor: borderHex,
                                            color: color,
                                            fontSize: '0.6rem',
                                            fontWeight: 600
                                        }}
                                    />
                                </Paper>
                            </Grid>
                        );
                    })}
                </Grid>
            </Paper>

            {/* Performance Table */}
            <Paper sx={{ p: 0 }}>
                <Typography variant="h6" fontWeight={700} sx={{ p: 2, pb: 0 }}>
                    Detailed Performance Metrics
                </Typography>
                <TableContainer>
                    <Table size={isMobile ? "small" : "medium"}>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Seller</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Level</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Revenue</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Orders</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Products</TableCell>
                                <TableCell align="center" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem', display: { xs: 'none', sm: 'table-cell' } }}>Trend</TableCell>
                                <TableCell align="right" sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>Score</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sellers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((seller) => {
                                const level = getPerformanceLevel(seller);
                                const color = getPerformanceColor(level);
                                const mockTrend = getPerformanceTrend(
                                    seller.stats?.totalRevenue || 0, 
                                    (seller.stats?.totalRevenue || 0) * 0.9
                                );
                                
                                return (
                                    <TableRow key={seller._id} hover>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar
                                                    src={seller.profileImage}
                                                    sx={{ bgcolor: color, width: isMobile ? 32 : 40, height: isMobile ? 32 : 40 }}
                                                >
                                                    <Store sx={{ fontSize: isMobile ? 14 : 18 }} />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="subtitle2" fontWeight={600} sx={{ fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                                                        {seller.brandName || seller.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}>
                                                        {seller.email}
                                                    </Typography>
                                                    {/* Mobile performance display */}
                                                    <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1, mt: 0.5 }}>
                                                        <Chip
                                                            label={level.replace('_', ' ').toUpperCase()}
                                                            size="small"
                                                            sx={{ 
                                                                bgcolor: level === 'excellent' ? '#4CAF5020' : level === 'good' ? '#8BC34A20' : level === 'average' ? '#FF980020' : '#F4433620',
                                                                color: color,
                                                                fontSize: '0.6rem',
                                                                fontWeight: 600
                                                            }}
                                                        />
                                                        <Typography variant="caption" color="text.secondary">
                                                            PKR {(sellerOrdersData[seller._id]?.totalRevenue || 0).toLocaleString()}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Chip
                                                label={level.replace('_', ' ').toUpperCase()}
                                                size="small"
                                                sx={{ 
                                                    bgcolor: level === 'excellent' ? '#4CAF5020' : level === 'good' ? '#8BC34A20' : level === 'average' ? '#FF980020' : '#F4433620',
                                                    color: color,
                                                    fontWeight: 600
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Typography variant="body2" fontWeight={600} color={color}>
                                                PKR {(sellerOrdersData[seller._id]?.totalRevenue || 0).toLocaleString()}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {sellerOrdersData[seller._id]?.totalOrders || 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {seller.stats?.totalProducts || 0}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                                                {getPerformanceIcon(mockTrend.trend)}
                                                <Typography variant="caption" fontWeight={600}>
                                                    {mockTrend.value.toFixed(1)}%
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={level === 'excellent' ? 100 : level === 'good' ? 75 : level === 'average' ? 50 : 25}
                                                    sx={{ 
                                                        width: 60, 
                                                        bgcolor: '#e0e0e0',
                                                        '& .MuiLinearProgress-bar': { bgcolor: color }
                                                    }}
                                                />
                                                <Typography variant="caption" fontWeight={600} color={color}>
                                                    {level === 'excellent' ? 'A+' : level === 'good' ? 'B+' : level === 'average' ? 'C+' : 'D'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Pagination */}
            {sellers.length > rowsPerPage && (
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={sellers.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            )}
        </Box>
    );
};

export default SellerPerformance;
